import { AVAILABILITY_STATUSES } from 'constants/fieldData';
import { FC, useCallback, useMemo } from 'react';
import {
  type Availability,
  combineSlots,
  getAvailabilityStatus,
  mapBookingItemsToSlots,
  separateSlot,
} from 'utils/booking';
import moment from 'utils/dayjs';
import BookingSlot from '../bookingSlot';

interface Slot {
  startTime: string;
  endTime: string;
}

interface Space {
  id: number;
}

interface Props {
  bookingItems?: Slot[];
  availability?: Availability;
  slots: Slot[];
  showSlotTimes?: boolean;
  spaces?: Space[];
  disabled?: boolean;
  loading?: boolean;
  horizontal?: boolean;
  relativeSizeAndPosition?: boolean;
  // May be undefined while the venue is still loading; slot offsets are only
  // meaningful once it resolves.
  openingTime?: string;
  value?: Slot[];
  onChange?: (value: Slot[]) => void;
  setTouched?: (value: boolean) => void;
}

const slotNe =
  (slot: Slot) =>
  ({ startTime, endTime }: Slot) =>
    slot.startTime !== startTime || slot.endTime !== endTime;

const fixSlot = ({ startTime, endTime }: Slot): Slot => ({
  startTime,
  endTime,
});

const emptyList: never[] = [];
const emptyObject: Availability = {};
const noOp = () => {};

const BookingSlots: FC<Props> = ({
  bookingItems = emptyList,
  availability = emptyObject,
  slots = emptyList,
  showSlotTimes = true,
  spaces = emptyList,
  disabled = false,
  loading = false,
  horizontal = false,
  relativeSizeAndPosition = false,
  value: rawValue = emptyList,
  onChange: setValue = noOp,
  setTouched = noOp,
  openingTime,
}) => {
  // rawValue might contain combined slots for existing bookings
  const value = useMemo(
    () => rawValue.flatMap(slot => separateSlot(slot, slots)).map(fixSlot),
    [rawValue, slots]
  );

  const readOnly = setValue === noOp;

  const isSelected = (slot: Slot) => !value.every(slotNe(slot));
  const deselect = (slot: Slot) => setValue(value.filter(slotNe(slot)));
  const select = (slot: Slot) => setValue([...value, slot]);
  const toggleSelected = (slot: Slot) =>
    isSelected(slot) ? deselect(slot) : select(slot);

  const isBooked = (bookingSlot: Slot) =>
    mapBookingItemsToSlots(bookingItems).some(
      bookingItem =>
        moment(bookingItem.startTime, 'HH:mm').isBefore(
          moment(bookingSlot.endTime, 'HH:mm')
        ) &&
        moment(bookingItem.endTime, 'HH:mm').isAfter(
          moment(bookingSlot.startTime, 'HH:mm')
        )
    );

  const combinedSlots = useMemo(() => combineSlots(value), [value]);

  const slotAvailabilityStatus = useCallback(
    (bookingSlot: Slot) => {
      const { startTime, endTime } =
        combinedSlots.find(
          slot =>
            moment(bookingSlot.startTime, 'HH:mm').isSameOrAfter(
              moment(slot.startTime, 'HH:mm')
            ) &&
            moment(bookingSlot.endTime, 'HH:mm').isSameOrBefore(
              moment(slot.endTime, 'HH:mm')
            )
        ) || bookingSlot;

      if (disabled) {
        return AVAILABILITY_STATUSES.PROVISIONAL;
      }

      return getAvailabilityStatus(
        availability,
        startTime,
        endTime,
        spaces.map(({ id }) => id)
      );
    },
    [availability, disabled, spaces, combinedSlots]
  );

  const wrapperClass = horizontal
    ? 'booking-slots__wrapper-horizontal'
    : 'booking-slots__wrapper';

  const overlayClass = horizontal
    ? 'booking-slots__overlay-horizontal'
    : 'booking-slots__overlay';

  const renderSlot = (bookingSlot: Slot) => {
    const availabilityStatus = slotAvailabilityStatus(bookingSlot);

    const onClick = () => {
      setTouched(true);
      toggleSelected(fixSlot(bookingSlot));
    };

    return (
      <BookingSlot
        key={`${bookingSlot.startTime}-${bookingSlot.endTime}`}
        openingTime={openingTime}
        startTime={bookingSlot.startTime}
        endTime={bookingSlot.endTime}
        showSlotTimes={showSlotTimes}
        availabilityStatus={availabilityStatus}
        selected={isSelected(bookingSlot)}
        disabled={!isSelected(bookingSlot) && isBooked(bookingSlot)}
        onClick={readOnly ? undefined : onClick}
        relativeSizeAndPosition={relativeSizeAndPosition}
      />
    );
  };

  return (
    <div className="booking-slots">
      {loading && (
        <div className="field__loading" data-testid="availabilityLoader" />
      )}

      <ol className={wrapperClass}>{slots.map(renderSlot)}</ol>

      {!readOnly && disabled && (
        <div className={overlayClass} data-testid="bookingSlotsOverlay" />
      )}
    </div>
  );
};

export default BookingSlots;
